import { cloneDeep } from 'lodash';
import React, { useEffect } from 'react';
import { useLazyLoadQuery } from 'react-relay';
import { graphql } from 'relay-runtime';
import useVTOStore from 'src/components/pages/virtualTryOn/store';
import { toGlobalID, toLocalGuid } from 'src/shared/helpers';
import usePreview3DStore from '../store';
import { tryOnGraphqlMapReverse, TryOnType } from '../tryOnTypes';
import { existingSceneDataWithSceneQuery } from './__generated__/existingSceneDataWithSceneQuery.graphql';

export const ExistingSceneData = ({ children, onSceneLoaded }: { children: React.ReactNode; onSceneLoaded: (isNew: boolean) => void }) => {
    const [saveVersion, zkProduct, zkScene, setZkScene] = usePreview3DStore((store) => [store.saveVersion, store.zkProduct, store.zkScene, store.setZkScene]);
    const isDamMode = useVTOStore((x) => x.isDamMode);

    // Get scene (conditional but it will never change in this case)
    const query = useLazyLoadQuery<existingSceneDataWithSceneQuery>(
        graphql`
            query existingSceneDataWithSceneQuery($sceneId: String!) {
                scene(id: $sceneId) {
                    id
                    sceneIDforPagination
                    sceneID
                    tempSceneID
                    name
                    fileObjectID
                    url
                    rootUrl
                    sceneFileName
                    fileLength
                    manifestFileObjectID
                    manifestFileUrl
                    manifestFileLength
                    incrementalsFileObjectID
                    incrementalsFileUrl
                    incrementalsFileLength
                    sellerID
                    imageUrl
                    imageData
                    imageFileLength
                    useDefaultEnvironmentTexture
                    cameraPositionScale
                    cameraLimitLowerRadius
                    version
                    usedTextureUrls
                    config {
                        general {
                            backgroundAlpha
                            backgroundColor
                            removeDefaultBackground
                            environmentTexture
                            environmentOptions {
                                mipMaps
                                generateHarmonics
                                gammaSpace
                                prefilterOnLoad
                                rotationY
                                intensity
                                pbrIntensity
                            }
                            matchSameNameTextures
                            antialiasEnabled
                            tryOnEnabled
                            tryOnSettings {
                                type
                                styleButton
                                buttonCustomizations {
                                    image
                                    textLabel
                                    textColor
                                    backgroundColor
                                    borderColor
                                    borderWidth
                                    borderRadius
                                }
                                typeSettings {
                                    try_on
                                    pd
                                    active
                                    mandatoryPD
                                }
                            }
                            arEnabled
                            arOnTheFly
                            arAnchor
                            arScale
                            meshSize {
                                height
                                width
                                depth
                            }
                            frameDAMSettings
                            useDelayedTextureLoading
                            useDelayedMaterialLoading
                            useMaterialOffloading
                            prefetchMeshes
                            prefetchMaterials
                            exposure
                            contrast
                            colorCurvesHue
                            colorCurvesDensity
                            colorCurvesSaturation
                            colorCurvesBrightness
                            vignetteEnabled
                            vignetteWeight
                            vignetteColor
                            preventDefaultLights
                            forceDracoCompression
                            fitToScreenMargin
                            antialias {
                                desktop {
                                    enabled
                                    inRotationEnabled
                                }
                                mobile {
                                    enabled
                                    inRotationEnabled
                                }
                            }
                        }
                        environment {
                            sizeAuto
                            mainColor
                            backgroundColor
                            skybox {
                                createSkybox
                                size
                                texture
                                extensions
                                files
                                color
                                blur
                            }
                            ground {
                                createGround
                                size
                                texture
                                color
                                opacity
                                enableShadow
                                shadowLevel
                                enableMirror
                                mirrorBlurKernel
                            }
                            shadows {
                                enableShadows
                                groundSizeMultiplierWidth
                                groundSizeMultiplierLength
                                shadowMapSize
                                shadowHexColor
                                contactHardeningUVRatio
                                useContactHardeningShadow
                                useBlurCloseExponentialShadowMap
                                usePercentageCloserFiltering
                                useKernelBlur
                                usePoissonSampling
                                blurBoxOffset
                                blurScale
                                blurKernel
                                depthScale
                                shadowDarkness
                                shadowMinZ
                                shadowMaxZ
                            }
                        }
                        lights {
                            directionalLightBack {
                                position {
                                    x
                                    y
                                    z
                                }
                                general {
                                    intensity
                                    visible
                                }
                            }
                            directionalLightLeft {
                                position {
                                    x
                                    y
                                    z
                                }
                                general {
                                    intensity
                                    visible
                                }
                            }
                            pointLightBack {
                                position {
                                    x
                                    y
                                    z
                                }
                                general {
                                    intensity
                                    visible
                                }
                            }
                            pointLightFront {
                                position {
                                    x
                                    y
                                    z
                                }
                                general {
                                    intensity
                                    visible
                                }
                            }
                            hemisphereLight {
                                position {
                                    x
                                    y
                                    z
                                }
                                general {
                                    intensity
                                    visible
                                }
                            }
                        }
                        camera {
                            autoRotate
                            rotationSpeed
                            changeRotationDirection
                            contrast
                            exposure
                            toneMappingEnabled
                            toneMappingType
                            setInitialZoomToBoundingBox
                            initialZoomToBBInertiaDamping
                            initialZoomToBBModelSizeRadius
                            initialZoomToBBScrollZoomEnabled
                            initialZoomToBBScrollLowerLimit
                            initialZoomToBBScrollUpperLimit
                            positionScale
                            limitLowerRadius
                            zoomEnabled
                            useMinZoomPerc
                            minZoomPerc
                            useMaxZoomPerc
                            maxZoomPerc
                            lowerAlphaLimit
                            upperAlphaLimit
                            lowerBetaLimit
                            upperBetaLimit
                            fixedAlphaAngle
                            fixedBetaAngle
                            useLowerAlphaLimit
                            useUpperAlphaLimit
                            useLowerBetaLimit
                            useUpperBetaLimit
                            position {
                                alpha
                                beta
                                radius
                                fov
                            }
                            animateIntro
                            zoom
                        }
                        glow {
                            enabled
                            intensity
                            blurSize
                        }
                        infoPoints {
                            enabled
                            pointsStyle {
                                borderColor
                                innerPointColor
                                color
                                highlightColor
                            }
                        }
                        uiConfig {
                            urlARIcon
                            urlLoader
                        }
                        textures {
                            format
                            webpOptions {
                                type
                                quality
                            }
                        }
                        model {
                            rotation {
                                x
                                y
                                z
                            }
                            position {
                                x
                                y
                                z
                            }
                            scale {
                                x
                                y
                                z
                            }
                            config {
                                doubleSided
                            }
                        }
                        animations {
                            runAtStart
                            loop
                            speed
                        }
                    }
                    sources {
                        sourceID
                        fileObjectID
                        url
                        rootUrl
                        sceneFileName
                        fileLength
                        textures {
                            textureID
                            fileObjectID
                            url
                            fileLength
                        }
                    }
                    cameraLocations {
                        locationID
                        name
                        px
                        py
                        pz
                        tx
                        ty
                        tz
                    }
                    materialMappings {
                        sceneMaterialID
                        libraryMaterialID
                    }
                    textures {
                        textureID
                        materialID
                        isDefaultEnvironment
                        fileObjectID
                        url
                        fileLength
                        usage
                    }
                    materialPhotos {
                        photoID
                        materialID
                        displayOrder
                        fileObjectID
                        url
                        fileLength
                    }
                    shaderMaterials {
                        materialID
                        name
                        vertex
                        fragment
                    }
                    meshLocations {
                        locationID
                        name
                        tempImageFileKey
                        tempImageFileUrl
                        imageUrl
                        displayOrder
                        isCollapsed
                        isExploded
                        adjustCamera
                        data
                    }
                }
            }
        `,
        {
            sceneId: toGlobalID(zkProduct!.get('scenes').at(0).get('sceneID')!, 'Scene')
        },
        { fetchPolicy: 'network-only', fetchKey: saveVersion }
    );

    useEffect(() => {
        const zkScene = new Zakeke.Scene(cloneDeep(query.scene)); // Clone to make it writable
        // Disable delayed material loading as default, because design materials ust be loaded asap
        zkScene.setUseDelayedMaterialLoading(true);
        zkScene.set('sceneID', toLocalGuid(query.scene.id)!);

        // Convert graphql tryon type to number
        const tryOnSettings = query.scene.config?.general?.tryOnSettings;

        if (tryOnSettings) {
            zkScene.get('config')!.get('general')!.get('tryOnSettings')!.type =
                tryOnGraphqlMapReverse.get(tryOnSettings.type ?? 'EYEWEAR') ?? TryOnType.Eyewear;
        }

        if (isDamMode) {
            const frameDamSettings = query.scene.config?.general?.frameDAMSettings;
            if (frameDamSettings) {
                zkScene.get('config')!.get('general')!.set('frameDAMSettings', frameDamSettings);
            }
        }

        setZkScene(zkScene);
        onSceneLoaded(false);
    }, [query.scene, saveVersion]);

    if (!zkScene) return null;

    return children;
};
